import React, { useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FullScreenLayout } from '../../routes/layouts/FullScreenLayout';
import { FullScreenHeader } from '@/components/shared/layout';
import { useAuth } from '../../contexts/AuthContext';
import { useClassDetail } from '@/data/queries/useClasses';
import { classDetailDtoToClassItem } from '@/utils/classUtils';
import { handleApiError } from '@/utils/errorHandlers';
import { ApiError } from '@/services/apiClient';

type TabType = 'curriculum' | 'evaluation' | 'practice-status' | 'ranking' | 'statistics';

export default function SessionManagement(): React.ReactElement {
    const { t } = useTranslation('class');
    const { classId: classIdParam } = useParams<{ classId: string }>();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { getCurrentRole } = useAuth();

    const role = getCurrentRole();
    const isManagerPath = role === 'master' || role === 'admin';
    const basePath = role === 'master' ? '/master' : '/admin';

    const classId = parseInt((classIdParam ?? '').trim(), 10);
    const currentTab = (searchParams.get('tab') as TabType) || 'curriculum';

    const {
        data: classDetailDto,
        isError,
        error: queryError,
    } = useClassDetail(classId, {
        enabled: classId > 0,
    });

    const classItem = classDetailDto ? classDetailDtoToClassItem(classDetailDto) : null;

    const backToClassDetailPath = (): string => {
        if (isManagerPath) {
            return `${basePath}/class-management/${classId}`;
        }
        return `/student/my-classes/${classId}`;
    };

    // 커리큘럼 탭 선택 시 첫 번째 세션으로 자동 리다이렉트
    useEffect(() => {
        if (currentTab === 'curriculum' && classItem?.curriculum?.length) {
            const firstSession = classItem.curriculum[0];
            const sessionPath = isManagerPath
                ? `${basePath}/class-management/${classId}/curriculum/${firstSession.id}`
                : `/class/${classId}/curriculum/${firstSession.id}`;
            navigate(sessionPath, { replace: true });
        }
    }, [currentTab, classId, classItem, isManagerPath, basePath, navigate]);

    const handleTabChange = (tab: TabType) => {
        if (tab === 'curriculum') {
            // 커리큘럼 탭으로 이동 (첫 번째 세션으로 리다이렉트될 것임)
            setSearchParams({ tab });
        } else if (tab === 'evaluation') {
            // 평가결과 화면으로 이동
            navigate(`${basePath}/class-management/${classId}/evaluation`);
        } else if (tab === 'practice-status') {
            // 실습현황 화면으로 이동
            navigate(`${basePath}/class-management/${classId}/practice-status`);
        } else if (tab === 'statistics') {
            // 실습통계 화면으로 이동
            navigate(`${basePath}/class-management/${classId}/statistics`);
        } else if (tab === 'ranking') {
            // 랭킹 화면으로 이동
            navigate(`${basePath}/class-management/${classId}/ranking`);
        } else {
            // 다른 탭으로 이동
            setSearchParams({ tab });
        }
    };

    const tabs = [
        { id: 'curriculum', label: '커리큘럼' },
        { id: 'evaluation', label: '평가결과' },
        { id: 'practice-status', label: '실습현황' },
        { id: 'ranking', label: '랭킹' },
        { id: 'statistics', label: '실습통계' },
    ] as const;

    const renderCenterContent = () => {
        return (
            <div className="flex gap-2">
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => handleTabChange(tab.id as TabType)}
                        className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                            currentTab === tab.id
                                ? 'bg-blue-500 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>
        );
    };

    if (!classDetailDto && !isError) {
        return (
            <FullScreenLayout
                header={
                    <FullScreenHeader title="…" onBack={() => navigate(backToClassDetailPath())} />
                }
            >
                <div className="flex items-center justify-center h-full text-gray-500">불러오는 중…</div>
            </FullScreenLayout>
        );
    }

    if (isError || !classItem) {
        const fallback = t('detail.classNotFound');
        let message = fallback;
        if (queryError instanceof ApiError) {
            if (queryError.status === 401) {
                message = t('detail.classLoadAuthRequiredPrivate');
            } else if (queryError.status === 403) {
                message = t('detail.classLoadForbiddenPrivate');
            } else {
                message = handleApiError(queryError, fallback).message;
            }
        }
        return (
            <FullScreenLayout
                header={
                    <FullScreenHeader 
                        title="세션 관리" 
                        onBack={() => navigate(backToClassDetailPath())} 
                        centerContent={renderCenterContent()}
                    />
                }
            >
                <div className="flex items-center justify-center h-full text-gray-500">{message}</div>
            </FullScreenLayout>
        );
    }

    // 커리큘럼 탭은 useEffect에서 자동 리다이렉트되므로 여기서는 렌더링하지 않음
    const renderTabContent = () => {
        switch (currentTab) {
            case 'evaluation':
            case 'practice-status':
            case 'ranking':
            case 'statistics':
                return (
                    <div className="flex items-center justify-center h-full text-gray-500">
                        준비 중입니다
                    </div>
                );
            default:
                // 커리큘럼 탭은 리다이렉트되므로 여기 도달하지 않음
                return null;
        }
    };

    return (
        <FullScreenLayout
            header={
                <FullScreenHeader 
                    title={classItem.title} 
                    onBack={() => navigate(backToClassDetailPath())} 
                    centerContent={renderCenterContent()}
                />
            }
        >
            {/* 탭 컨텐츠 영역 */}
            <div className="flex-1 overflow-auto">
                {renderTabContent()}
            </div>
        </FullScreenLayout>
    );
}
